import { useEffect, useState } from "react";

/**
 * Test seam: force the app to believe it is offline. The simulator can't flip
 * `navigator.onLine` (it follows the Mac's network) and iOS 26 blocks WebView
 * JS injection, so the only way to exercise the offline paths on a real
 * WKWebView is to bake this in at build time (`VITE_E2E_FORCE_OFFLINE=true`),
 * or set the `e2eForceOffline` localStorage flag (e.g. from the e2e harness).
 *
 * Prod-safety: the build-time flag is checked first and is inherently safe —
 * a real prod/staging build never sets `VITE_E2E_FORCE_OFFLINE`, so Vite
 * statically folds that branch to `false` and strips it. (Do NOT gate this
 * branch behind `import.meta.env.PROD !== true`: `vite build` runs in
 * production mode, which would dead-strip the flag we rely on.) The runtime
 * localStorage escape hatch IS gated to non-prod so it can't be flipped by an
 * end user in a shipped build — except under `VITE_E2E_TEST_SEAMS=true`
 * (the e2e prod-build mode; real deploys never set it).
 */
function forcedOffline(): boolean {
  if (import.meta.env?.VITE_E2E_FORCE_OFFLINE === "true") {
    return true;
  }

  if (
    import.meta.env?.PROD === true &&
    import.meta.env?.VITE_E2E_TEST_SEAMS !== "true"
  ) {
    return false;
  }

  try {
    return (
      typeof localStorage !== "undefined" &&
      localStorage.getItem("e2eForceOffline") === "true"
    );
  } catch {
    return false;
  }
}

/**
 * Non-hook online check for use in async/non-React contexts (storage adapters,
 * loaders, event handlers). In the Capacitor WebView and all browsers,
 * `navigator.onLine` plus the `online`/`offline` events are reliable.
 */
export function getIsOnline(): boolean {
  if (forcedOffline()) {
    return false;
  }

  if (typeof navigator === "undefined") {
    return true;
  }

  return navigator.onLine;
}

/** React hook tracking the device's online/offline status. */
export function useNetworkStatus(): { isOnline: boolean } {
  const [isOnline, setIsOnline] = useState(getIsOnline);

  useEffect(() => {
    function handleOnline() {
      setIsOnline(true);
    }

    function handleOffline() {
      setIsOnline(false);
    }

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return { isOnline };
}
